import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import commonEN from '../locales/en/common.json';
import activitiesEN from '../locales/en/activities.json';
import profileEN from '../locales/en/profile.json';
import notificationsEN from '../locales/en/notifications.json';
import formsEN from '../locales/en/forms.json';

import commonDE from '../locales/de/common.json';
import activitiesDE from '../locales/de/activities.json';
import profileDE from '../locales/de/profile.json';
import notificationsDE from '../locales/de/notifications.json';
import formsDE from '../locales/de/forms.json';

// Domain-based language detection
const detectLanguageFromDomain = (): string => {
  const hostname = window.location.hostname;
  
  // Check for German domain
  if (hostname.includes('treffpunkts.de')) {
    return 'de';
  }
  
  // Check for English domain (.com or any other)
  if (hostname.includes('treffpunkts.com') || hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 'en';
  }
  
  // Default to English for other domains
  return 'en';
};

// Custom language detector that prioritizes user preference, then domain
const customLanguageDetector = {
  name: 'customDetector',
  lookup: () => {
    // 1. Check localStorage for user preference (highest priority)
    const userPreference = localStorage.getItem('treffpunkts-language');
    if (userPreference && ['en', 'de'].includes(userPreference)) {
      return userPreference;
    }
    
    // 2. Check domain-based detection
    const domainLanguage = detectLanguageFromDomain();
    
    // 3. Do NOT persist domain detection automatically; let explicit user choice persist
    return domainLanguage;
  },
  cacheUserLanguage: (lng: string) => {
    localStorage.setItem('treffpunkts-language', lng);
  }
};

// Create a detector instance and register our custom detector BEFORE init
const detector = new LanguageDetector();
detector.addDetector(customLanguageDetector);

// Initialize i18next
i18n
  .use(detector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: commonEN,
        activities: activitiesEN,
        profile: profileEN,
        notifications: notificationsEN,
        forms: formsEN,
      },
      de: {
        common: commonDE,
        activities: activitiesDE,
        profile: profileDE,
        notifications: notificationsDE,
        forms: formsDE,
      },
    },
    
    // Custom language detection
    detection: {
      order: ['customDetector', 'localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'treffpunkts-language',
    },
    
    // Fallback language
    fallbackLng: 'en',
    
    // Default namespace
    defaultNS: 'common',
    
    // Debug in development
    debug: process.env.NODE_ENV === 'development',
    
    // Interpolation settings
    interpolation: {
      escapeValue: false, // React already does escaping
    },
    
    // React specific settings
    react: {
      useSuspense: false, // We'll handle loading states manually
    },
  });

// (custom detector already registered on the detector instance above)

// Export language change function for easy access
export const changeLanguage = (language: string) => {
  i18n.changeLanguage(language);
  localStorage.setItem('treffpunkts-language', language);
};

// Export current language getter
export const getCurrentLanguage = () => {
  const language = i18n.language || 'en';
  // Extract base language code (e.g., "en-GB" -> "en", "de-DE" -> "de")
  return language.split('-')[0].toLowerCase();
};

// Export supported languages
export const supportedLanguages = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
];

// Helper function to convert language code to API format
export const getApiLanguageCode = (languageCode?: string): 'EN' | 'DE' => {
  const language = languageCode || getCurrentLanguage();
  const baseLanguage = language.split('-')[0].toLowerCase();
  return baseLanguage === 'de' ? 'DE' : 'EN';
};

export default i18n;